import { Link } from 'react-router-dom'
import { ShoppingCart, SlidersHorizontal, Tag } from 'lucide-react'
import { useCart } from '../context/Cart'
import { useShop } from '../context/Shop'
import { eur, imageFit, imageUrl, ttc } from '../lib/format'
import type { Product } from '../lib/types'

export function PromoBadge({ p, large = false }: { p: Product; large?: boolean }) {
  if (!p.price.rate) return null
  return (
    <span className={`absolute bottom-3 left-3 flex items-center gap-1 bg-red-600 text-white ${large ? 'text-sm px-3 py-1.5' : 'text-xs px-2.5 py-1'} font-extrabold rounded-md shadow-lg`}>
      <Tag className={large ? 'w-4 h-4' : 'w-3.5 h-3.5'} />{p.price.promo_label}
    </span>
  )
}

export default function ProductCard({ product: p, club = '' }: { product: Product; club?: string }) {
  const { sportInfo } = useShop()
  const { add, setOpen, toast } = useCart()
  const sport = sportInfo(p.sport)
  const href = `/produit/${p.id}${club ? `?club=${club}` : ''}`
  const r = p.price
  return (
    <article className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
      <Link to={href} className="relative h-52 overflow-hidden bg-slate-100 block">
        <img src={imageUrl(p.images[0], 500)} alt={p.title} loading="lazy" className={`w-full h-full ${imageFit(p.images[0])} group-hover:scale-105 transition-transform duration-500`} />
        {p.badge && <span className="absolute top-3 left-3 bg-navy-900/90 text-white text-[10px] font-bold uppercase px-2.5 py-1 rounded-md shadow">{p.badge}</span>}
        <span className="absolute top-3 right-3 bg-white/90 text-navy-900 text-[10px] font-bold px-2 py-1 rounded-md">{sport.emoji} {sport.label}</span>
        <PromoBadge p={p} />
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{p.brand}</span>
        <h3 className="font-bold text-navy-900 text-sm leading-snug mt-0.5"><Link to={href} className="hover:text-brand-orange transition-colors">{p.title}</Link></h3>
        <p className="text-xs text-slate-500 mt-2 line-clamp-2 flex-1">{p.short_desc}</p>
        <div className="flex items-end justify-between gap-2 mt-4 pt-4 border-t border-slate-100">
          <div>
            {r.varies && <div className="text-[10px] font-semibold text-slate-500 uppercase">à partir de</div>}
            <div className={`font-display text-xl font-bold ${r.rate ? 'text-red-600' : 'text-navy-900'}`} data-testid={`product-price-${p.id}`}>{eur(ttc(r.final_ht))} <span className="text-xs font-sans font-semibold text-slate-500">TTC</span></div>
            {r.rate ? <div className="text-[11px] text-slate-500"><s>{eur(ttc(r.from_ht))}</s></div> : null}
          </div>
          {p.options.length ? (
            <Link to={href} className="flex items-center justify-center gap-1.5 bg-navy-900 hover:bg-brand-ink text-white text-xs font-bold px-3.5 py-2.5 rounded-lg transition-colors" data-testid={`product-choose-${p.id}`}><SlidersHorizontal className="w-4 h-4" /> Choisir</Link>
          ) : (
            <button type="button" onClick={() => { add({ id: p.id, qty: 1, variant: {}, flocage: false, club }); toast('Article ajouté au panier'); setOpen(true) }} className="flex items-center justify-center gap-1.5 bg-brand-ink hover:bg-orange-800 text-white text-xs font-bold px-3.5 py-2.5 rounded-lg transition-colors" data-testid={`product-add-${p.id}`}><ShoppingCart className="w-4 h-4" /> Ajouter</button>
          )}
        </div>
      </div>
    </article>
  )
}
